import type { PlaceOrderResponse } from "../../apis/order";
import { useNavigation } from "../../hooks";
import { useTranslation } from "react-i18next";
import { Text, type CardAdvanceModel, type CardAdvanceRowProps } from "../../components";
import { Constants } from "../../utils";
import { SVGs } from "../../assets";
import { format } from "date-fns";
import * as React from "react";

export type PaymentViewModel = {
  data: PlaceOrderResponse;
  t: (key: string, options?: Record<string, unknown>) => string;
  leftCards: CardAdvanceModel[];
  billingItems: CardAdvanceRowProps[];
  returnsAndExchangesUrl: string;
  onPay: () => Promise<void>;
};

export function useViewModel(): PaymentViewModel {
  const { t } = useTranslation();
  const navigation = useNavigation();
  const data = navigation.data as PlaceOrderResponse;
  const { order, stripeCheckOutUrl } = data;
  const orderItem = order?.order_items?.[0] as any;

  const serviceDateTime =
    orderItem?.serviceDateTime ?? orderItem?.serviceDate ?? undefined;

  const bookingSummaryCard: CardAdvanceModel = React.useMemo(
    () => ({
      title: t("general.bookingSummary"),
      subtitle: (
        <span className="mt-[8px] inline-block text-text-secondary font-[700]">
          {t("general.ref")}{" "}
          <span className="font-[400]">{order?.code || order?.id || "-"}</span>
        </span>
      ),
      items: [
        {
          icon: SVGs.locationMarker,
          label: orderItem?.partnerLocation?.code ?? "--",
          value:
            orderItem?.partnerLocation?.nearbyPlaces?.[0]?.name ??
            orderItem?.partnerLocation?.city?.name ??
            "--",
        },
        {
          icon: SVGs.shield,
          label: orderItem?.product?.partner_service?.service?.name ?? "--",
        },
        !!serviceDateTime && {
          icon: SVGs.calendarScheduled,
          label: serviceDateTime
            ? format(new Date(serviceDateTime), "MMM dd, HH:mm")
            : "-",
        },
      ].filter(Boolean) as CardAdvanceRowProps[],
      footer: (
        <div className="px-[16px]">
          <div className="h-[1px] w-full bg-divider" />
          <div className="py-[16px] flex items-center justify-between">
            <Text level={4}>{t("general.payableAmount")}</Text>
            <Text level={4}>€{order?.payableAmount ?? "-"}</Text>
          </div>
        </div>
      ),
    }),
    [
      order?.code,
      order?.id,
      order?.payableAmount,
      serviceDateTime,
      orderItem,
      t,
    ],
  );

  const memberCards: CardAdvanceModel[] = React.useMemo(() => {
    return (order?.order_items ?? []).map((item: any, index: number) => ({
      title:
        (order?.order_items?.length ?? 0) > 1
          ? t("general.memberInformationIndexed", { index: index + 1 })
          : t("general.memberInformation"),
      items: [
        {
          icon: SVGs.avatarRound,
          value:
            `${item.visitorFirstName ?? ""} ${item.visitorLastName ?? ""}`.trim(),
        },
        { icon: SVGs.envelope, value: item.visitorEmailAddress ?? "-" },
        { icon: SVGs.telephone, value: item.visitorPhoneNumber ?? "-" },
        {
          icon: SVGs.barcode,
          value: `${item.insuranceProviderName} - ${item.insurancePolicyNumber}`,
        },
      ],
    }));
  }, [order?.order_items, t]);

  const bookerCard: CardAdvanceModel = React.useMemo(
    () => ({
      title: t("general.bookerInformation"),
      items: [
        {
          icon: SVGs.avatarRound,
          value:
            `${order?.bookerFirstName ?? ""} ${order?.bookerLastName ?? ""}`.trim() ||
            "-",
        },
      ],
    }),
    [order?.bookerFirstName, order?.bookerLastName, t],
  );

  const billingItems: CardAdvanceRowProps[] = React.useMemo(
    () => [
      {
        icon: SVGs.avatarRound,
        value:
          `${order?.billingPersonFirstName ?? ""} ${order?.billingPersonLastName ?? ""}`.trim() ||
          "-",
      },
      { icon: SVGs.envelope, value: order?.billingPersonEmail || "-" },
      { icon: SVGs.telephone, value: order?.billingPersonContactNumber || "-" },
    ],
    [
      order?.billingPersonContactNumber,
      order?.billingPersonEmail,
      order?.billingPersonFirstName,
      order?.billingPersonLastName,
    ],
  );

  const onPay = React.useCallback(async () => {
    try {
      if (stripeCheckOutUrl) window.location.href = stripeCheckOutUrl;
    } catch {}
  }, [stripeCheckOutUrl]);

  return {
    t,
    data,
    onPay,
    billingItems,
    leftCards: [bookingSummaryCard, ...memberCards, bookerCard],
    returnsAndExchangesUrl: Constants.urls.returnsAndExchanges,
  };
}
